



/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import './ChargerOnVersionTrend.css';
import { useAppDispatch, useAppSelector } from '../../../rmsReduxStore/rmsReduxHooks';
import { getVersionReleasesReducer, getReferenceDataReducer, setFilterData, getFilterSelectedValue } from '../../../rmsReduxStore/reduxExports';
import { SelectDropDown, useEffectOnce } from '../../../globalUtils/globalExports';
import { formatNumberWithCommas } from '../../../globalUtils/globalHooks';
import { RectangularSkeleton } from '../../../globalUtils/SkeletonDesign/SkeletonDesign';
import { Icon } from '../../../globalUtils/globalIcons';
import CustomPulseLoader from './CustomPulseLoader';

interface ChargerOnVersionTrendProp {
  handleFilter: Function,
  getFilterValue: Function,
  fromPopUp?: boolean,
  popupHandler: React.Dispatch<React.SetStateAction<boolean>>
}

const ChargerOnVersionTrend: React.FC<ChargerOnVersionTrendProp> = (props) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { handleFilter, getFilterValue, fromPopUp, popupHandler } = props;
  const { versionReleasesSoftwareVersionReferenceData } = useAppSelector(getReferenceDataReducer);
  const { chargerOnVersionTrendInfo,chargerOnVersionTrendLoader } = useAppSelector(getVersionReleasesReducer);
  const [filterValue, setFilterValue] = useState<IGeneralReferenceData | null>(null);
  const dispatch = useAppDispatch();
  const navigationUpperLimit = fromPopUp?100:9;
  const [visibleMin, setVisibleMin] = useState(0);
  const [visibleMax, setVisibleMax] = useState(navigationUpperLimit);
  const chartHeight = fromPopUp ? 600 : 300;

  const barColorsPalette = [
    '#1D4F71', '#35B879', '#734B5E', '#455C85', '#9F7F91'
  ]


  //clear the filters
  useEffect(() => {
    setFilterValue(getFilterSelectedValue('softwareVersion', 'versionReleasesDashboard', false));
  }, [versionReleasesSoftwareVersionReferenceData, chargerOnVersionTrendInfo])

  /**
 * Converts HSL color values to a HEX string.
 * @param hue - Hue (0–360)
 * @param saturation - Saturation percentage (0–100)
 * @param lightness - Lightness percentage (0–100)
 * @returns HEX color string (e.g. "#aabbcc")
 */
  // const hslToHex = (hue: number, saturation: number, lightness: number): string => {
  //   saturation /= 100;
  //   lightness /= 100;

  //   const k = (n: number) => (n + hue / 30) % 12;
  //   const chroma = saturation * Math.min(lightness, 1 - lightness);
  //   const component = (n: number) =>
  //     lightness - chroma * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  //   const toHex = (value: number) =>
  //     Math.round(value * 255).toString(16).padStart(2, '0');

  //   const hexColor = `#${toHex(component(0))}${toHex(component(8))}${toHex(component(4))}`;
  //   return hexColor;
  // }

  /**
   * Generates a stable HEX color for a given string.
   * Same string will always map to the same color.
   *
   * @param text - Input string (e.g. category name)
   * @param saturation - (optional) saturation % for vividness
   * @param lightness - (optional) lightness % for brightness
   * @returns HEX color string
   */
  // const generateColorFromString = (
  //   text: string,
  //   saturation: number = 65,
  //   lightness: number = 55
  // ): string => {
  //   // FNV-1a 32-bit hash for stable distribution
  //   let hash = 2166136261 >>> 0;
  //   for (let i = 0; i < text.length; i++) {
  //     hash ^= text.charCodeAt(i);
  //     hash = Math.imul(hash, 16777619) >>> 0;
  //   }
  //   const hue = hash % 360; // Map hash to a hue (0–359)
  //   return hslToHex(hue, saturation, lightness);
  // }


  const getFormattedData = useCallback(() => {
    const data = chargerOnVersionTrendInfo || [];

    // // apply filter if any
    // if (filterValue && filterValue.value && filterValue.value !== 'All') {
    //   data = data.filter((item: any) => item.version === filterValue.value);
    // }

    const slicedData = data.slice(visibleMin, visibleMax);

    const xAxis = slicedData.map((item: any) => item?.version);
    const yAxis = slicedData.map((item: any) => item?.chargersCount);

    return { xAxis, yAxis };
  }, [chargerOnVersionTrendInfo, visibleMin, visibleMax, filterValue]);

  const dataLength = useMemo(() => {
    return chargerOnVersionTrendInfo?.length ?? 0;
  }, [chargerOnVersionTrendInfo])

  const handleNavigation = (direction: 'left' | 'right') => {
    const step = navigationUpperLimit;
    if (direction === 'left') {
      setVisibleMin(Math.max(visibleMin - step, 0));
      setVisibleMax(Math.max(visibleMax - step, step));
    } else {
      setVisibleMin(Math.min(visibleMin + step, dataLength - step));
      setVisibleMax(Math.min(visibleMax + step, dataLength));
    }
  };

  const xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject) {
    const rawValue = this.value;
    return `<div style="text-align:center;">${rawValue}</div>`;
  };

  const labelStep = 1;
  const { xAxis, yAxis } = getFormattedData();
  const totalBars = xAxis.length;


  const options: Highcharts.Options = {
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      height: chartHeight,
      spacing: [20, 10, 0, 0],
      events: {
        render: function () {
          const chart = this as Highcharts.Chart & any;

          // Remove existing custom arrows before adding new ones
          const existingArrows = chart.container.getElementsByClassName('custom-arrow');
          while (existingArrows.length > 0) {
            existingArrows[0].remove();
          }

          // Left arrow (shifted more inside by changing left position)
          if (visibleMin > 0) {
            chart.renderer.image(
              '/images/left_arrow_highchart.svg',
              chart.plotLeft - 10, // shifted inside
              chart.plotTop + chart.plotHeight / 2 - 10,
              20,
              20
            )
              .addClass('custom-arrow left')
              .on('click', () => handleNavigation('left'))
              .add();
          }

          // Right arrow (shifted more inside by changing left position)
          if (visibleMax < dataLength - 1) {
            chart.renderer.image(
              '/images/right_arrow_highchart.svg',
              chart.plotLeft + chart.plotWidth - 15, // shifted inside
              chart.plotTop + chart.plotHeight / 2 - 10,
              20,
              20
            )
              .addClass('custom-arrow right')
              .on('click', () => handleNavigation('right'))
              .add();
          }
        }
      }
    },
    title: { text: undefined },
    subtitle: { text: undefined },
    xAxis: {
      title: {
        text: 'Software Versions'
      },
      gridLineDashStyle: 'ShortDash',
      categories: getFormattedData()?.xAxis,
      lineColor: '#005EFF',
      tickColor: '#005EFF',
      plotBands: getFormattedData().xAxis.map((_, index) => {
        let barWidthFraction = 0.25;
        if (dataLength < 4 && dataLength > 0) barWidthFraction = [0.06, 0.12, 0.18][dataLength - 1]
        return {
          from: index - barWidthFraction,
          to: index + barWidthFraction,
          color: '#f5f5f5'
        }
      }),
      labels: {
        rotation: -45,
        step: labelStep,
        formatter: xAxisLabelFormatter,
      },
    },
    yAxis: {
      title: { text: 'No. of Chargers' },
      gridLineDashStyle: 'ShortDash',
      tickAmount: 8,
      min: 0,
    },
    tooltip: {
      formatter: function () {
        const category = this.x;
        const value = this.y;
        // \u25CF ---> ●
        return `<span style="font-size:12px">${category}</span><br/>
        <span style="color:${this.point.color}">\u25CF</span>
        Chargers: <b>${formatNumberWithCommas(value || 0)}</b>`;
      },
    },
    legend: { enabled: false },
    plotOptions: {
      column: {
        borderRadius: 8,
        maxPointWidth: (dataLength > 3) ? undefined : 50
      }
    },
    series: [
      {
        name: 'Chargers',
        type: 'column',
        data: xAxis.map((version: string, i: number) => ({
          name: version,
          y: yAxis[i],
          color: barColorsPalette[i % barColorsPalette.length], // ✅ stable color per version
        })),
        colorByPoint: false
      },
    ],
    credits: { enabled: false },
    exporting: { enabled: false },
  };

  const handleChargerOnVersionTrendFilter =
    async (val: any, filterKey: string): Promise<void> => {
      const res = await setFilterData(val, filterKey, 'versionReleasesDashboard', 'SET', false)(dispatch);
      if (res?.message === 'Action dispatched successfully') {
        handleFilter(res?.filters);
      }
    }

  const handleSelectChange = (val: any): void => {
    setFilterValue(val);
    handleChargerOnVersionTrendFilter(val, 'softwareVersion');
    handleChartPageReset();
  };

  const handleChartPageReset = () => {
    setVisibleMin(0);
    setVisibleMax(navigationUpperLimit);
  };

  //   useEffect(() => {
  //   const chart = chartRef.current?.chart;
  //   if (!chart) return;

  //   const categories = getFormattedData().xAxis;
  //   if (!categories.length) return;

  //   const categoryWidthPx = chart.plotWidth / categories.length;
  //   const barWidthPx = Math.min(categoryWidthPx * 0.8, 50); // 80% of slot but max 50px
  //   const barWidthFraction = (barWidthPx / categoryWidthPx) / 2;

  //   chart.xAxis[0].update({
  //     plotBands: categories.map((_, index) => ({
  //       from: index - barWidthFraction,
  //       to: index + barWidthFraction,
  //       color: '#f5f5f5'
  //     }))
  //   }, false);

  //   chart.redraw();
  // }, [chargerOnVersionTrendInfo, visibleMin, visibleMax, filterValue]);

  const preventClickEventPropagation = (e) => {
    e.stopPropagation();
  }


  return (
    <div className={fromPopUp ? 'charger-version-chart-popup' : 'charger-version-chart'}
      onClick={preventClickEventPropagation}>
      <div className="chart-header">
        <div className="charger-version-trend-chart-header">
          <div className='charger-version-trend-chart-header-heading'>Charger Count v/s Software Version</div>
          <div className='charger-version-trend-chart-expansion-icon'
            onClick={() => popupHandler(prev => !prev)}>
            {
              fromPopUp ? <Icon
                containerClassName="charger__version__trend__chart__shrink-icon"
                iconClassName="side__popover__shrink__icon"
              /> :
                <Icon
                  containerClassName="charger__version__trend__chart__expand-icon"
                  iconClassName="side__popover__expand__icon"
                />
            }
          </div>
        </div>
        <div className="chart-filters">
          <SelectDropDown
            selectValue={filterValue}
            selectOptions={versionReleasesSoftwareVersionReferenceData ?? []}
            handleSelectOptionChange={handleSelectChange}
            selectDropDownId="version-filter-dropdown"
            dropDownClassName="version-filter-dropdown"
            isMultiSelect={true}
            placeholder={'Select Version'}
          />
        </div>
      </div>
      <div className="charger-version-trend-chart-body">
        {(chargerOnVersionTrendLoader || !chargerOnVersionTrendInfo )? (
          <div className="charger-version-trend-chart-loader" style={{ height: `${chartHeight}px` }}>
            <CustomPulseLoader showLoader={true} size={fromPopUp?40:20}/>
          </div>
        ) : chargerOnVersionTrendInfo?.length === 0 ? (
          <div className="no-data-text" style={{ height: `${chartHeight}px` }}>No data available</div>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
        )}
      </div>
    </div>
  );
};

export default ChargerOnVersionTrend;